import { useState, useEffect, useCallback, useMemo } from 'react';
import type { Race, RaceFilter } from '../types';
import { raceService } from '../services';
import { fetchRaces } from '../services/api/races';

interface UseRaceDataResult {
  races: Race[];
  filteredRaces: Race[];
  loading: boolean;
  error: Error | null;
  retry: () => void;
  filter: RaceFilter;
  setFilter: (filter: RaceFilter) => void;
  selectedRace: Race | null;
  setSelectedRace: (race: Race | null) => void;
  hoveredRaceId: string | null;
  setHoveredRaceId: (id: string | null) => void;
  regionCounts: Record<string, number>;
}

export function useRaceData(countries: string[] = ['korea']): UseRaceDataResult {
  const [races, setRaces] = useState<Race[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [filter, setFilter] = useState<RaceFilter>({});
  const [selectedRace, setSelectedRace] = useState<Race | null>(null);
  const [hoveredRaceId, setHoveredRaceId] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  const countriesKey = countries.join(',');

  useEffect(() => {
    let cancelled = false;

    async function loadData() {
      try {
        setLoading(true);
        setError(null);
        const data = await fetchRaces();
        if (!cancelled) {
          setRaces(data);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err : new Error('Failed to load races'));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadData();

    return () => {
      cancelled = true;
    };
  }, [countriesKey, retryCount]);

  const retry = useCallback(() => {
    setRetryCount((n) => n + 1);
  }, []);

  const filteredRaces = useMemo(
    () => raceService.filterRaces(races, filter),
    [races, filter],
  );
  const regionCounts = useMemo(() => raceService.getRegionCounts(races), [races]);

  const handleSetFilter = useCallback((newFilter: RaceFilter) => {
    setFilter(newFilter);
  }, []);

  const handleSetSelectedRace = useCallback((race: Race | null) => {
    setSelectedRace(race);
  }, []);

  const handleSetHoveredRaceId = useCallback((id: string | null) => {
    setHoveredRaceId(id);
  }, []);

  return {
    races,
    filteredRaces,
    loading,
    error,
    retry,
    filter,
    setFilter: handleSetFilter,
    selectedRace,
    setSelectedRace: handleSetSelectedRace,
    hoveredRaceId,
    setHoveredRaceId: handleSetHoveredRaceId,
    regionCounts,
  };
}
